/**
 * AgentsModePulse — verifier-mode title + pulse rail.
 *
 * Persistent title + gutter Pulse for every `/agents/*` product surface.
 * Mounted from `AgentsProductShell` (Slice 1/6).
 */

import { PulseGutterRail, SectionTitleBar } from "@excellent/ui";
import { usePathname, useSearch } from "@excellent/ui/router";
import { getMode } from "@/modes/modes-registry";

const SEGMENT_TO_BAND: Record<string, string> = {
  workflows: "fleet",
  analytics: "lens",
};

const SEGMENT_TO_LABEL: Record<string, string> = {
  analytics: "Legacy analytics",
  claims: "Developer claims",
  health: "Legacy health",
  lanes: "Developer lanes",
  qa: "Developer QA",
  triage: "Developer triage",
  workflows: "Legacy workflows",
};

function leafLabel(seg: string): string {
  if (!seg) return "Verification";
  return SEGMENT_TO_LABEL[seg] ?? seg.charAt(0).toUpperCase() + seg.slice(1);
}

export function AgentsModePulse({
  dynamic,
  gutter = true,
}: {
  dynamic?: boolean;
  /** When false, only the title bar mounts (pulse flags off). */
  gutter?: boolean;
}) {
  const pathname = usePathname() ?? "/agents";
  const search = useSearch();
  const area = search?.get?.("area") ?? "";
  const segs = pathname.replace(/^\/agents\/?/, "").split("/").filter(Boolean);
  const seg = segs[0] ?? "";
  const isRoot = !area && (seg === "" || seg === "overview");
  const current = area || SEGMENT_TO_BAND[seg] || seg;
  const title = isRoot ? "Verification" : leafLabel(area || seg);
  const isVerifierProductSurface =
    isRoot ||
    seg === "verification" ||
    seg === "verification-ops" ||
    area === "verification" ||
    area === "readiness";

  // Workflow detail owns its identity under PageHeader — don't stack shell title.
  const pageOwnsIdentity =
    seg === "workflows" &&
    segs.length === 2 &&
    !["new", "compiled"].includes(segs[1]!);

  if (pageOwnsIdentity) {
    return gutter ? (
      <PulseGutterRail scope={`agents:${current}`} dynamic={dynamic} />
    ) : null;
  }

  return (
    <div className={`flex flex-col ${isRoot ? "gap-7" : "gap-4"}`}>
      <SectionTitleBar
        title={title}
        description={isRoot ? getMode("agents")?.description : undefined}
      />
      {gutter && !isVerifierProductSurface ? (
        <PulseGutterRail
          scope={isRoot ? "agents" : `agents:${current}`}
          dynamic={dynamic}
        />
      ) : null}
    </div>
  );
}
